'use strict';

define('vb/private/stateManagement/appPackage',[
  'vb/helpers/mixin',
  'vb/private/constants',
  'vb/private/stateManagement/appPackageExtension',
  'vb/private/stateManagement/containerRjsInitMixin',
  'vb/private/stateManagement/packageFlow',
  'vb/private/stateManagement/context/appPackageContext',
  'vb/private/utils',
  'vbsw/private/serviceWorkerManager',
], (Mixin, Constants, AppPackageExtension, ContainerRjsInitMixin, PackageFlow, AppPackageContext,
  Utils, ServiceWorkerManager) => {
  /**
   * @typedef {import('vb/private/stateManagement/page.js').default} Page
   * @typedef {import('vb/private/vx/extension.js').default} Extension
   */

  /**
   * AppPackage class
   */
  class AppPackage extends Mixin(PackageFlow).with(ContainerRjsInitMixin) {
    /**
     * AppPackage constructor
     *
     * @param  {Extension}  extension the extension that contain this App UI
     * @param  {Object}     appUiInfo the metadata for this App UI
     * @param  {Page}       page      the page that contain this App UI
     */
    constructor(extension, appUiInfo, page, className = 'AppPackage') {
      const { id } = appUiInfo;
      super(id, page, `${Constants.DefaultPaths.APPLICATIONS}${id}/`, className);

      // Override value defined in Container
      this._extension = extension;

      /**
       * @type {Object}
       * @private
       */
      this._appUiInfo = appUiInfo;

      /**
       * @type {Promise|null}
       * @private
       */
      this._initializePromise = null; // Initialized in initialize()
    }

    /**
     * The name is always app, app.json, app.js
     * Override value defined in Container
     *
     * @type {String}
     */
    // eslint-disable-next-line class-methods-use-this
    get name() {
      return 'app';
    }

    /**
     * @type {AppPackage}
     */
    get package() {
      return this;
    }

    /**
     * This is the appUiInfo metadata from the digest, not the vb/AppUiInfos object
     * @type {Object}
     */
    get appUiInfo() {
      return this._appUiInfo;
    }

    /**
     * The default event prefix is the lowercase class name (see container.js) but for
     * appPackage we want to use the same event prefix as application
     *
     * @type {String}
     */
    // eslint-disable-next-line class-methods-use-this
    get eventPrefix() {
      return 'application';
    }

    /**
     * The name of the runtime environment function to be used to load the descriptor
     *
     * @type {String} the descriptor loader function name
     */
    static get descriptorLoaderName() {
      return 'getAppUiDescriptor';
    }

    /**
     * The name of the runtime environment function to be used to load the module functions
     *
     * @type {String} the module loader function name
     */
    static get functionsLoaderName() {
      return 'getAppUiFunctions';
    }

    static get extensionClass() {
      return AppPackageExtension;
    }

    /**
     * returns the AppPackageContext constructor used to create the '$' expression context
     * @type {typeof AppPackageContext}
     */
    static get ContextType() {
      return AppPackageContext;
    }

    /**
     * Enables implicit mapping of the resources folder for App UI.
     *
     * @type {boolean}
     */
    // eslint-disable-next-line class-methods-use-this
    get implicitResourceFolder() {
      return true;
    }

    /**
     * Override the flow createService to define the services from the right place.
     * @return {Promise}
     */
    createServices() {
      return Promise.resolve()
        .then(() => {
          // App UI services are the services defined in the extension containing
          // this App UI.
          this.services = this.application.extensions[this.extensionId].services;
        });
    }

    /**
     * The name of the resource to be used to load the App UI.
     * @type {string}
     */
    get fullName() {
      return this.name;
    }

    /**
     * Use the App UI urlId for the id in the URL
     * Override the value in Container
     * @type {!String}
     */
    get urlId() {
      return this.appUiInfo.urlId || super.urlId;
    }

    /**
     * @return {Boolean}
     */
    // eslint-disable-next-line class-methods-use-this
    isNavigable() {
      // App UIs are always navigable
      return true;
    }

    /**
     * Return true if the id is the id of the default flow for this App UI
     * @param {string} id
     * @return {Boolean}
     */
    isDefaultFlowId(id) {
      return this.definition.defaultFlow === id;
    }

    updateCurrentAppUiBuiltinVariable() {
      // Assign the $global.currentAppUi variable
      // Uses the variable setValueInternal because it's a readonly variable and the regular
      // assignment will fail.
      const currenAppUiVar = this.application.scope.getVariable(Constants.GLOBAL_CURRENT_APPUI_VARIABLE,
        Constants.VariableNamespace.BUILTIN);
      currenAppUiVar.setValueInternal(this.appUiInfo);
    }

    /**
     * Initializes the appPackage
     *
     * @return {Promise}
     */
    initialize() {
      // Cache the initializePromise because initialize is not only called by enter(),
      // it's also called by nested packagePage when invoking the beforeEnterEvent
      this._initializePromise = this._initializePromise || super.initialize();

      return this._initializePromise;
    }

    /**
     * Called by the router in response to the enter callback
     * Implemented by Page and Flow
     *
     * @return {Promise}
     */
    enter() {
      return Promise.resolve().then(() => {
        // Update the builtin variable before executing enter
        this.updateCurrentAppUiBuiltinVariable();
        return super.enter();
      });
    }

    initDefault(definition) {
      const def = definition;

      return super.initDefault(def);
    }

    // eslint-disable-next-line class-methods-use-this
    defineCurrentPageBuiltinVariable() {
      return null;
    }

    // eslint-disable-next-line class-methods-use-this
    updateFlowCurrentPageVariable() {
      // no-op
    }

    loadDefaultPageOrFlow(navContext) {
      const defaultContainerId = this.router.defaultStateId;
      if (defaultContainerId) {
        let pagePath = navContext.options.page || '';
        // 2 cases to consider:
        //   * the  App UI does not have a root page, this is when defaultFlow property is defined and in
        //     this case, the flow parameter replace the default flow
        //   * there is a root page and possibly sibling pages and in this case, the page parameter
        //     replaces the default root page
        if (this.definition.defaultFlow) {
          let flowId;
          if (pagePath[0] === '/') {
            const segments = pagePath.split('/');
            // the flowId is the second segment since the split makes first segment an empty string
            [, flowId] = segments;
            // The path to the page is what's left of the path
            pagePath = segments.slice(2).join('/');
          } else {
            flowId = navContext.options.flow || defaultContainerId;
          }

          return this.loadContainer(flowId, navContext)
            .then((flow) => flow && flow.getLeafPageInstance(pagePath || flow.router.defaultStateId, navContext));
        }

        const flowId = navContext.options.flow;
        if (flowId) {
          // Load the root page (default page of the App UI)
          return this.loadContainer(defaultContainerId, navContext)
            .then((page) => page && page.getLeafPageInstanceFromFlowId(flowId, navContext));
        }

        if (!pagePath) {
          return this.loadDefaultContainers(navContext);
        }

        // For the case when using a root page, make the navigation relative to the current AppUi
        if (pagePath[0] === '/') {
          pagePath = pagePath.substr(1);
        }

        return this.getLeafPageInstance(pagePath, navContext);
      }

      this.log.error(`Cannot navigate to App UI ${this.id} because the default page or flow is not defined.`);
      return undefined;
    }

    /**
     * Overridden to support loading offline handler defined in an app ui.
     *
     * @returns {*}
     */
    load() {
      return super.load()
        .then(() => {
          const swMgrInstance = ServiceWorkerManager.getInstance();

          // uninstall existing offline handler if any
          return swMgrInstance.uninstallSecondaryOfflineHandler()
            .then(() => this.getOfflineHandlerUrl())
            .then((offlineHandlerUrl) => {
              if (offlineHandlerUrl) {
                return swMgrInstance.installSecondaryOfflineHandler(offlineHandlerUrl);
              }

              return undefined;
            })
            .then(() => {
              // If this appUi is offline-enabled, tell the service worker manager to run
              // any pending setup and caching.
              // But don't block on it!
              if (this._extension.offlineEnabled) {
                swMgrInstance.runPendingSetupActions();
              }
            });
        });
    }

    /**
     * Returns the requirejs path for the class in which the offline handler is defined. If the offline
     * handler is defined in the app.js, the path for app.js is returned. Otherwise, call
     * RuntimeEnvironment.getOfflineHandlerUrl to give DT a chance to install its own offline handler
     * which is used for caching data for performance optimization purpose.
     *
     * @returns {Promise<String>}
     */
    getOfflineHandlerUrl() {
      return Promise.resolve().then(() => {
        // If createOfflineHandler function is defined in app.js, return the resource path for app.js.
        if (this.functions && typeof this.functions.createOfflineHandler === 'function') {
          return this.getResourcePath();
        }

        // Otherwise, give DT a chance to install its own offline handler for caching data.
        return Utils.getRuntimeEnvironment().then((re) => re.getOfflineHandlerUrl());
      });
    }

    /**
     * App UI have the ability to skip their root page when the defaultFlow property
     * is in the definition instead of defaultPage
     *
     * @return {Promise}
     */
    processDefaultPage() {
      return Promise.resolve().then(() => {
        const flowId = this.definition.defaultFlow;
        // If defaultFlow is defined, do not process the default page,
        // just set the state of the router and return
        if (flowId) {
          this.router.defaultStateId = flowId;
          return undefined;
        }

        // Otherwise just proceed
        return super.processDefaultPage();
      });
    }

    /**
     * Retrieve the cached instance of the nested container.
     * @param  {String} id the id of the page to retrieve
     * @return {Object} the page instance
     */
    getContainer(id) {
      return this.flows[id] || super.getContainer(id);
    }

    /**
     * Load the nested container given its id
     *
     * @param  {String} id the id of the container
     * @param  {Object} [navContext] the context of the current navigation chain
     * @return {Promise} a promise that resolve to a container instance
     */
    loadContainer(id, navContext) {
      if (navContext && navContext.isCancelled()) {
        return Promise.resolve();
      }

      const flowId = this.definition.defaultFlow;
      if (flowId) {
        return this.loadFlowFromId(id, navContext);
      }

      return super.loadContainer(id, navContext);
    }

    loadFlow() {
      // Loads the extension bundle first
      return this.extension.init().then(() => super.loadFlow());
    }

    getScopeResolverMap() {
      return {
        [Constants.APP_UI_PREFIX]: this,
        [Constants.GLOBAL_PREFIX]: this.application,
      };
    }

    dispose() {
      // Dispose the router, other wise the next state change will call into this instance
      if (this.router) {
        this.router.dispose();
        this.router = null;
      }

      // For the case of an AppUi with no root page, dispose of the nested flows.
      Object.values(this.flows).forEach((flow) => flow.dispose());

      super.dispose();
    }
  }

  return AppPackage;
});

